import Model, { attr } from '@ember-data/model'
import { Type } from '@warp-drive/core-types/symbols'
import { service } from '@ember/service'
import type { IntlService } from 'ember-intl'
import type StringTransform from 'teacher-ui-client/transforms/string'
import type NumberTransform from 'teacher-ui-client/transforms/number'
import type DateTransform from 'teacher-ui-client/transforms/date'

export interface AssignmentDetails {
  assignment_id: string
  category?: string
  grade_position: string
  grade_lesson_number?: string
  book_name?: string
  title?: string
  chapters?: number[]
  description?: string
}

interface AssignedStudent {
  canonical_student_id: number
  status: string
}

export interface AssignmentWithChapters extends Assignment {
  assignedChapters: number[]
  assignmentDetails: Assignment['assignmentDetails'] & {
    chapters: number[]
    book_name: string
  }
}

export function hasChapters(assignment: Assignment): assignment is AssignmentWithChapters {
  return assignment.assignedChapters !== undefined
}

export type AssignmentType =
  | 'lesson'
  | 'book'
  | 'chapter'
  | 'read_aloud_book'
  | 'read_aloud_chapter'
  | 'ewi'
  | 'long-write'

export type AssignmentPrecinct =
  | 'lessons'
  | 'spelling'
  | 'clinker_castle'
  | 'driving_tests'
  | 'my_lessons'
  | 'english_skills_spelling'
  | 'library'
  | 'ewi'
  | 'long-writes'

export default class Assignment extends Model {
  @service intl!: IntlService

  @attr<DateTransform>('date') declare startAt: Date

  @attr<DateTransform>('date') declare endAt: Date

  // We don't receive this value from the backend, but we are able to set it to expire the assignment.
  @attr<DateTransform>('date') expiredAt: Date | undefined

  @attr<StringTransform>('string') declare product:
    | LegacyProductRe
    | LegacyProductRex
    | LegacyProductMs
    | LegacyProductWl
    | LegacyProductFp

  @attr<StringTransform>('string') declare precinct: AssignmentPrecinct

  // Difference between assigner & teacher seems to be that a subco may create an assignment on behalf of a teacher.
  // In that case, assigner is subco, teacher is the other teacher.

  @attr<NumberTransform>('number') declare assignerId: number

  @attr<NumberTransform>('number') declare teacherId: number

  @attr<NumberTransform>('number') declare studentsCompleted: number

  @attr<NumberTransform>('number') declare studentsTotal: number

  get isExpired() {
    return this.status === 'expired'
  }

  get isCompleted() {
    return this.status === 'completed'
  }

  @attr<StringTransform>('string') declare status: string

  @attr<StringTransform>('string') declare assignmentType: AssignmentType

  get isLibraryQuizAssignment() {
    return ['book', 'chapter'].includes(this.assignmentType)
  }

  get isReadAloudAssignment() {
    return ['read_aloud_book', 'read_aloud_chapter'].includes(this.assignmentType)
  }

  get isLibraryAssignment() {
    return this.isLibraryQuizAssignment || this.isReadAloudAssignment
  }

  get isWritingAssignment() {
    // Writing Legends assignments
    return this.assignmentType === 'long-write' || this.assignmentType === 'ewi'
  }

  @attr() declare assignedStudents: AssignedStudent[]

  @attr() declare assignmentDetails: AssignmentDetails

  get title() {
    const assignmentDescription = this.intl.t(`assignments.description.${this.product}.${this.precinct}`)
    if (this.product === 'maths' && this.precinct === 'driving_tests') {
      return `${assignmentDescription} ${this.assignmentDetails.grade_lesson_number}`
    } else if (typeof this.assignmentDetails.book_name === 'string') {
      return this.assignmentDetails.book_name
    } else if (typeof this.assignmentDetails.title === 'string') {
      return this.assignmentDetails.title
    }
    return `${assignmentDescription} ${this.assignmentDetails.assignment_id}`
  }

  get assignedChapters() {
    return this.assignmentDetails.chapters
  }

  [Type] = 'assignment' as const
}
